import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { GitBranch, Search, Save, Loader2, Check, X, Trash2, Download, Sparkles, MoreVertical, AlertTriangle, CheckCheck, Eraser } from 'lucide-react';

/**
 * Settings → Comment ↔ Roof Type Matrix.
 *
 * Editor for the relationship between prescripted comments and roof types,
 * organised by inspection category. Goes beyond a basic checkbox grid with
 * bulk-apply (per column), keyword-based suggested links, CSV export, and
 * per-row + per-column delete.
 */

const CATEGORIES = [
  'Ridging', 'Tiles', 'Valleys', 'Barging', 'Spouting',
  'Sheeting', 'Fastenings', 'Flashings',
  'Roof Condition', 'Roof Recommendation', 'Appraisal Condition',
];

// Keyword → roof_type heuristic used by "Suggested links". Matches against
// the comment body + name; if any keyword hits, we suggest the linked roof
// types. Generic phrases that mention no specific material get NO
// suggestion (they should remain "generic = all roofs").
const SUGGESTION_RULES = [
  { keywords: ['clay'],                                                   roofs: ['Clay'] },
  { keywords: ['decramastic', 'decra'],                                   roofs: ['Decramastic'] },
  { keywords: ['old concrete'],                                           roofs: ['Old Concrete'] },
  { keywords: ['modern concrete'],                                        roofs: ['Modern Concrete'] },
  { keywords: ['concrete tile', 'concrete'],                              roofs: ['Modern Concrete', 'Old Concrete'] },
  { keywords: ['long run iron', 'longrun', 'long-run'],                   roofs: ['Long Run Iron'] },
  { keywords: ['short run iron', 'shortrun'],                             roofs: ['Short Run Iron'] },
  { keywords: ['colorsteel', 'colour steel', 'color steel'],              roofs: ['Long Run Iron', 'Short Run Iron'] },
  { keywords: ['iron', 'steel', 'corrugate'],                             roofs: ['Long Run Iron', 'Short Run Iron', 'Iron'] },
  { keywords: ['butynol'],                                                roofs: ['Butynol'] },
  { keywords: ['polycarbonate'],                                          roofs: ['Polycarbonate'] },
  { keywords: ['asbestos'],                                               roofs: ['Asbestos'] },
  { keywords: ['metal tile', 'tilcor', 'metrotile', 'gerard'],            roofs: ['Metal Tile'] },
  { keywords: ['shake', 'shingle'],                                       roofs: ['Shake/Shingle'] },
  { keywords: ['slate'],                                                  roofs: ['Slate'] },
];

function suggestRoofsFor(script) {
  const txt = `${script.name || ''} ${script.body || script.body_text || ''}`.toLowerCase();
  const hits = new Set();
  SUGGESTION_RULES.forEach(({ keywords, roofs }) => {
    if (keywords.some(k => txt.includes(k))) {
      roofs.forEach(r => hits.add(r));
    }
  });
  return Array.from(hits);
}

export default function ConditionScriptMatrixPage() {
  const { api } = useAuth();
  const [scripts, setScripts] = useState([]);
  const [roofTypes, setRoofTypes] = useState([]);
  const [category, setCategory] = useState('Ridging');
  const [filter, setFilter] = useState('');
  const [rowState, setRowState] = useState({}); // {scriptId: 'saving' | 'saved' | 'error' }
  const [loading, setLoading] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(null); // roofType currently bulk-toggling
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [dupesOnly, setDupesOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get('/condition-scripts'),
        api.get('/dropdowns/roof_types').catch(() => ({ data: [] })),
      ]);
      setScripts(Array.isArray(a.data) ? a.data : []);
      const rt = Array.isArray(b.data) ? b.data : (b.data?.options || []);
      setRoofTypes(rt.filter(Boolean));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = {};
    scripts.forEach(s => {
      const k = s.inspection_category || s.category || '';
      if (!k) return;
      c[k] = (c[k] || 0) + 1;
    });
    return c;
  }, [scripts]);

  const categoryScripts = useMemo(() =>
    scripts.filter(s => (s.inspection_category || s.category) === category),
    [scripts, category]
  );

  // Detect duplicate comments within the current category — uses a
  // normalised name (lowercased + trimmed) so trivial whitespace/case
  // differences are flagged as dupes. Returns Set of script _ids that
  // share a name with at least one other comment in the same category.
  const duplicateIds = useMemo(() => {
    const byName = {};
    categoryScripts.forEach(s => {
      const key = (s.name || '').trim().toLowerCase();
      if (!key) return;
      (byName[key] = byName[key] || []).push(s._id);
    });
    const dupes = new Set();
    Object.values(byName).forEach(ids => {
      if (ids.length > 1) ids.forEach(id => dupes.add(id));
    });
    return dupes;
  }, [categoryScripts]);

  const visible = useMemo(() => {
    let list = categoryScripts;
    if (dupesOnly) list = list.filter(s => duplicateIds.has(s._id));
    if (!filter.trim()) return list;
    const needle = filter.toLowerCase();
    return list.filter(s =>
      (s.name || '').toLowerCase().includes(needle) ||
      (s.body || '').toLowerCase().includes(needle)
    );
  }, [categoryScripts, filter, dupesOnly, duplicateIds]);

  // Merge legacy single + new array fields → canonical resolved set.
  const resolved = useCallback((s) => {
    const arr = Array.isArray(s.roof_types) ? [...s.roof_types] : [];
    if (s.roof_type && !arr.includes(s.roof_type)) arr.push(s.roof_type);
    return arr;
  }, []);

  const toggle = async (script, rt, checked) => {
    const id = script._id;
    setRowState(prev => ({ ...prev, [id]: 'saving' }));
    const next = new Set(resolved(script));
    if (checked) next.add(rt); else next.delete(rt);
    const newArr = Array.from(next);
    const prevSnapshot = scripts;
    setScripts(prev => prev.map(s => s._id === id ? { ...s, roof_types: newArr, roof_type: '' } : s));
    try {
      await api.put(`/condition-scripts/${id}`, {
        category: script.category || '',
        name: script.name || '',
        body: script.body || script.body_text || '',
        roof_type: '',
        roof_types: newArr,
        inspection_category: script.inspection_category || '',
        recommendation: script.recommendation || '',
        sort_order: script.sort_order || 0,
        active: script.active !== false,
      });
      setRowState(prev => ({ ...prev, [id]: 'saved' }));
      setTimeout(() => setRowState(prev => { const { [id]: _drop, ...rest } = prev; return rest; }), 1600);
    } catch (e) {
      setRowState(prev => ({ ...prev, [id]: 'error' }));
      setScripts(prevSnapshot);
      alert(e?.response?.data?.detail || 'Save failed');
    }
  };

  // Bulk toggle every visible row for a column. Uses the dedicated backend
  // endpoint so we don't fire N parallel PUTs and risk rate-limit hiccups.
  const bulkColumn = async (rt, action) => {
    const verb = action === 'add' ? 'TICK' : 'UNTICK';
    if (!window.confirm(`${verb} "${rt}" for all ${visible.length} ${category} comment${visible.length === 1 ? '' : 's'} shown?`)) return;
    setBulkBusy(rt);
    try {
      await api.post('/condition-scripts/bulk-toggle', {
        inspection_category: category,
        roof_type: rt,
        action,
        script_ids: visible.map(s => s._id),
      });
      // Update locally to avoid the full reload flicker.
      setScripts(prev => prev.map(s => {
        if (!visible.find(v => v._id === s._id)) return s;
        const set = new Set(resolved(s));
        if (action === 'add') set.add(rt); else set.delete(rt);
        return { ...s, roof_types: Array.from(set), roof_type: s.roof_type === rt && action === 'remove' ? '' : s.roof_type };
      }));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Bulk update failed');
    }
    setBulkBusy(null);
  };

  // Bulk toggle every roof type for a single comment row in one PUT.
  // Used by the "Select all / Clear all" buttons next to each comment
  // name so the operator can flip the whole row in one click instead
  // of clicking 13 boxes one at a time. Reuses the same endpoint as
  // the per-cell toggle so server-side logic stays canonical.
  const bulkRow = async (script, action) => {
    const id = script._id;
    setRowState(prev => ({ ...prev, [id]: 'saving' }));
    const newArr = action === 'add' ? [...roofTypes] : [];
    const prevSnapshot = scripts;
    setScripts(prev => prev.map(s => s._id === id ? { ...s, roof_types: newArr, roof_type: '' } : s));
    try {
      await api.put(`/condition-scripts/${id}`, {
        category: script.category || '',
        name: script.name || '',
        body: script.body || script.body_text || '',
        roof_type: '',
        roof_types: newArr,
        inspection_category: script.inspection_category || '',
        recommendation: script.recommendation || '',
        sort_order: script.sort_order || 0,
        active: script.active !== false,
      });
      setRowState(prev => ({ ...prev, [id]: 'saved' }));
      setTimeout(() => setRowState(prev => { const { [id]: _drop, ...rest } = prev; return rest; }), 1600);
    } catch (e) {
      setRowState(prev => ({ ...prev, [id]: 'error' }));
      setScripts(prevSnapshot);
      alert(e?.response?.data?.detail || 'Save failed');
    }
  };

  // Delete a single comment row.
  const deleteComment = async (script) => {
    if (!window.confirm(`Permanently delete comment "${script.name || '(unnamed)'}"? This cannot be undone.`)) return;
    setRowState(prev => ({ ...prev, [script._id]: 'saving' }));
    try {
      await api.delete(`/condition-scripts/${script._id}`);
      setScripts(prev => prev.filter(s => s._id !== script._id));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
      setRowState(prev => { const { [script._id]: _d, ...rest } = prev; return rest; });
    }
  };

  // Delete a roof type globally — detaches it from every comment and removes
  // it from the dropdown options list. Comments that referenced it become
  // generic (no roof types), staying available on every roof's dropdown.
  const deleteRoofType = async (rt) => {
    const usageCount = scripts.reduce((n, s) => n + (resolved(s).includes(rt) ? 1 : 0), 0);
    if (!window.confirm(`Permanently delete roof type "${rt}"?\n\nIt is currently linked to ${usageCount} comment${usageCount === 1 ? '' : 's'}. Those comments will become generic (apply to all roofs).\n\nThis also removes "${rt}" from the dropdown lists everywhere. Cannot be undone.`)) return;
    setBulkBusy(rt);
    try {
      await api.delete(`/condition-scripts/roof-type/${encodeURIComponent(rt)}`);
      setRoofTypes(prev => prev.filter(x => x !== rt));
      setScripts(prev => prev.map(s => ({
        ...s,
        roof_type: s.roof_type === rt ? '' : s.roof_type,
        roof_types: (s.roof_types || []).filter(x => x !== rt),
      })));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Roof-type delete failed');
    }
    setBulkBusy(null);
  };

  // Accept ALL keyword suggestions for the current category — adds suggested
  // roof types onto every visible comment. Only adds (never removes) so the
  // user can't lose existing manual ticks.
  const acceptAllSuggestions = async () => {
    const toApply = [];
    visible.forEach(s => {
      const have = new Set(resolved(s));
      const sugg = suggestRoofsFor(s);
      const adds = sugg.filter(r => !have.has(r) && roofTypes.includes(r));
      if (adds.length) toApply.push({ id: s._id, adds });
    });
    if (!toApply.length) { alert('No new suggestions for the current view — all matches are already ticked.'); return; }
    if (!window.confirm(`Apply ${toApply.reduce((n, x) => n + x.adds.length, 0)} suggested links across ${toApply.length} comment${toApply.length === 1 ? '' : 's'}? Existing ticks are preserved.`)) return;
    setBulkBusy('__suggestions__');
    try {
      for (const { id, adds } of toApply) {
        const s = scripts.find(x => x._id === id);
        if (!s) continue;
        const merged = Array.from(new Set([...resolved(s), ...adds]));
        await api.put(`/condition-scripts/${id}`, {
          category: s.category || '', name: s.name || '',
          body: s.body || s.body_text || '',
          roof_type: '', roof_types: merged,
          inspection_category: s.inspection_category || '',
          recommendation: s.recommendation || '',
          sort_order: s.sort_order || 0,
          active: s.active !== false,
        });
      }
      await load();
    } catch (e) { alert('Bulk suggestion apply failed: ' + (e?.response?.data?.detail || e.message)); }
    setBulkBusy(null);
  };

  const exportCsv = () => {
    const url = `/condition-scripts/export.csv?inspection_category=${encodeURIComponent(category)}`;
    api.get(url, { responseType: 'blob' }).then(res => {
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl; a.download = `comment_matrix_${category.toLowerCase().replace(/\s+/g, '_')}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
    });
  };

  return (
    <div data-testid="condition-script-matrix-page" className="space-y-5">
      <div className="bg-white border border-[#E2E2DF] rounded-md p-6 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[3px] bg-[#1E40AF]" />
        <div className="flex items-start gap-4 justify-between">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-md bg-gradient-to-br from-[#1E40AF]/15 to-[#1E40AF]/5 flex items-center justify-center ring-1 ring-[#1E40AF]/10">
              <GitBranch size={22} className="text-[#1E40AF]" strokeWidth={2} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-0.5">Settings</p>
              <h1 className="text-2xl font-extrabold text-slate-900 font-[Manrope] tracking-tight">Comment ↔ Roof Type Matrix</h1>
              <p className="text-sm text-slate-500 mt-1 max-w-3xl">Edit which roof types each prescripted comment is linked to. Per-row ticks save immediately. Use bulk-apply (column header ⋮) or auto-suggestions to speed up initial setup.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              data-testid="toggle-suggestions"
              onClick={() => setShowSuggestions(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md transition-all border ${
                showSuggestions ? 'bg-violet-600 text-white border-violet-600 hover:bg-violet-700' : 'bg-white text-violet-700 border-violet-300 hover:bg-violet-50'
              }`}
            >
              <Sparkles size={12} /> {showSuggestions ? 'Hide' : 'Show'} suggestions
            </button>
            {showSuggestions && (
              <button data-testid="accept-suggestions" onClick={acceptAllSuggestions} disabled={bulkBusy === '__suggestions__'} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200 rounded-md disabled:opacity-50">
                {bulkBusy === '__suggestions__' ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Accept all suggestions
              </button>
            )}
            <button data-testid="export-csv" onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 rounded-md">
              <Download size={12} /> Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Category tabs */}
      <div className="bg-white border border-[#E2E2DF] rounded-md overflow-x-auto">
        <div className="flex items-center gap-0.5 p-2 min-w-max">
          {CATEGORIES.map(c => {
            const ct = counts[c] || 0;
            const active = c === category;
            return (
              <button
                key={c}
                data-testid={`tab-${c}`}
                onClick={() => setCategory(c)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                  active ? 'bg-[#1E40AF] text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {c}
                <span className={`text-[10px] font-mono-num rounded-full px-1.5 py-0.5 ${active ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>{ct}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-[#E2E2DF] rounded-md p-3 flex items-center gap-3 flex-wrap">
        <Search size={14} className="text-slate-400" />
        <input data-testid="matrix-filter" value={filter} onChange={e => setFilter(e.target.value)} placeholder={`Search ${category} phrases…`} className="flex-1 min-w-[200px] outline-none text-sm bg-transparent" />
        <button
          data-testid="toggle-dupes-only"
          onClick={() => setDupesOnly(v => !v)}
          title="Show only comments whose name is shared by another comment in this category"
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-all ${
            dupesOnly
              ? 'bg-amber-500 text-white border-amber-500'
              : duplicateIds.size > 0
                ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                : 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed'
          }`}
          disabled={duplicateIds.size === 0 && !dupesOnly}
        >
          <AlertTriangle size={11} /> {dupesOnly ? 'Showing duplicates' : `Find duplicates${duplicateIds.size ? ` (${duplicateIds.size})` : ''}`}
        </button>
        <span className="text-[11px] text-slate-400">{visible.length} of {counts[category] || 0}</span>
      </div>

      {loading ? (
        <div className="bg-white border border-[#E2E2DF] rounded-md p-12 text-center text-sm text-slate-400">
          <Loader2 size={20} className="animate-spin inline mr-2" /> Loading…
        </div>
      ) : visible.length === 0 ? (
        <div className="bg-white border border-[#E2E2DF] rounded-md p-12 text-center text-sm text-slate-400">No comments match.</div>
      ) : (
        <MatrixTable
          visible={visible}
          roofTypes={roofTypes}
          rowState={rowState}
          showSuggestions={showSuggestions}
          duplicateIds={duplicateIds}
          resolved={resolved}
          suggestRoofsFor={suggestRoofsFor}
          toggle={toggle}
          deleteComment={deleteComment}
          bulkBusy={bulkBusy}
          bulkColumn={bulkColumn}
          bulkRow={bulkRow}
          deleteRoofType={deleteRoofType}
        />
      )}

      <div className="bg-blue-50/60 border border-blue-200 rounded-md p-3 text-xs text-slate-600 flex items-start gap-2">
        <Save size={12} className="mt-0.5 text-[#1E40AF] flex-shrink-0" />
        <div>
          <strong>Tips:</strong> The 🗑 icon beside each comment name deletes that comment permanently. Click the <strong>⋮</strong> menu on any column header to bulk-tick / bulk-untick that roof type across visible rows, or to <strong>delete the roof type</strong> entirely.{' '}
          Use the amber <strong>Find duplicates</strong> button to flush out comments sharing the same name within a category.{' '}
          Toggle "Show suggestions" to preview keyword-based ticks (violet ring), then "Accept all suggestions" to apply them. Use the Condition Scripts page to add NEW comments.
        </div>
      </div>
    </div>
  );
}

// Matrix table — owns the sticky-header + synced top-scrollbar UX so
// the title row (IRON / LONGRUN / SHORT RUN / MODERN CONCRETE …) and
// the horizontal scroll sit at the OPERATOR'S sightline, not buried at
// the foot of the table where they have to scroll all the way down to
// reach.
//
// Two improvements over the previous flat <div overflow-x-auto> wrap:
//
// 1. **Sticky <thead>** — `position: sticky; top: 0` on the head row
//    pins the column titles to the viewport while the body scrolls
//    vertically. Backdrop is opaque + a faint shadow so the body can
//    pass cleanly underneath.
//
// 2. **Top horizontal scrollbar** — a sibling 12 px tall scroller is
//    mounted ABOVE the table. Its inner spacer matches the live table
//    width (measured via ResizeObserver) so its scroll thumb mirrors
//    the bottom one. We sync `scrollLeft` two-way with refs so dragging
//    either bar moves the other in lockstep.
function MatrixTable({
  visible, roofTypes, rowState, showSuggestions, duplicateIds,
  resolved, suggestRoofsFor, toggle, deleteComment,
  bulkBusy, bulkColumn, bulkRow, deleteRoofType,
}) {
  const topScrollRef = useRef(null);
  const bottomScrollRef = useRef(null);
  const tableRef = useRef(null);
  const [innerWidth, setInnerWidth] = useState(0);
  // Measure the rendered table width so the top spacer matches it.
  // ResizeObserver re-runs when columns are added / removed (e.g.
  // after deleting a roof type from the column menu).
  useLayoutEffect(() => {
    if (!tableRef.current) return;
    const el = tableRef.current;
    const update = () => setInnerWidth(el.scrollWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [roofTypes.length, visible.length]);

  const syncing = useRef(false);
  const onTopScroll = () => {
    if (syncing.current) { syncing.current = false; return; }
    if (bottomScrollRef.current && topScrollRef.current) {
      syncing.current = true;
      bottomScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
  };
  const onBottomScroll = () => {
    if (syncing.current) { syncing.current = false; return; }
    if (topScrollRef.current && bottomScrollRef.current) {
      syncing.current = true;
      topScrollRef.current.scrollLeft = bottomScrollRef.current.scrollLeft;
    }
  };

  return (
    <div className="bg-white border border-[#E2E2DF] rounded-md">
      {/* Top synced horizontal scrollbar — only shows when the table
          actually overflows. 12 px tall so the scrollbar handle is
          finger-friendly without taking too much vertical real estate. */}
      <div
        ref={topScrollRef}
        onScroll={onTopScroll}
        data-testid="matrix-top-scroll"
        className="overflow-x-auto overflow-y-hidden border-b border-[#E2E2DF]"
        style={{ height: 12 }}
      >
        <div style={{ width: innerWidth || 1, height: 1 }} />
      </div>
      <div
        ref={bottomScrollRef}
        onScroll={onBottomScroll}
        className="overflow-auto"
        style={{ maxHeight: 'calc(100vh - 280px)' }}
      >
        <table ref={tableRef} className="w-full text-sm">
          <thead className="sticky top-0 z-30 bg-slate-50/95 backdrop-blur-sm shadow-[0_2px_4px_rgba(0,0,0,0.05)]" data-testid="matrix-sticky-thead">
            <tr className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 border-b border-[#E2E2DF]">
              <th className="text-left p-3 sticky left-0 bg-slate-50/95 backdrop-blur-sm z-10 min-w-[260px]">Comment</th>
              {roofTypes.map(rt => (
                <ColumnHeader
                  key={rt}
                  roofType={rt}
                  busy={bulkBusy === rt}
                  onTickAll={() => bulkColumn(rt, 'add')}
                  onUntickAll={() => bulkColumn(rt, 'remove')}
                  onDelete={() => deleteRoofType(rt)}
                />
              ))}
              <th className="text-center px-3 py-3 w-12">Saved</th>
              <th className="text-center px-3 py-3 w-12">Delete</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F0EFEC]">
            {visible.map(s => {
              const set = new Set(resolved(s));
              const state = rowState[s._id];
              const suggested = showSuggestions ? new Set(suggestRoofsFor(s)) : new Set();
              const isDupe = duplicateIds.has(s._id);
              return (
                <tr key={s._id} data-testid={`matrix-row-${s._id}`} className={`hover:bg-slate-50/40 ${isDupe ? 'bg-amber-50/40' : ''}`}>
                  <td className={`p-3 sticky left-0 z-10 align-top ${isDupe ? 'bg-amber-50/60' : 'bg-white'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="font-bold text-slate-800 text-xs">{s.name || '(unnamed)'}</p>
                          {isDupe && (
                            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.12em] font-bold text-amber-800 bg-amber-100 border border-amber-300 rounded px-1 py-px">
                              <AlertTriangle size={9} /> Duplicate name
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500 leading-snug line-clamp-2 max-w-[240px] mt-0.5">{(s.body || '').slice(0, 140)}{(s.body || '').length > 140 ? '…' : ''}</p>
                        {!set.size && <span className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-[0.12em] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">Generic (all roofs)</span>}
                      </div>
                      <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
                        {/* Per-row bulk actions — operator can flip every
                            roof-type tick for this comment in one click
                            instead of clicking 13 boxes individually. */}
                        <button
                          data-testid={`bulk-row-tick-${s._id}`}
                          onClick={() => bulkRow(s, 'add')}
                          title="Tick every roof type for this comment"
                          disabled={set.size === roofTypes.length}
                          className="p-1 rounded text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 disabled:opacity-30 disabled:cursor-not-allowed border border-transparent hover:border-emerald-200 transition-colors"
                        >
                          <CheckCheck size={13} />
                        </button>
                        <button
                          data-testid={`bulk-row-clear-${s._id}`}
                          onClick={() => bulkRow(s, 'remove')}
                          title="Clear every roof type for this comment"
                          disabled={set.size === 0}
                          className="p-1 rounded text-slate-500 hover:text-slate-800 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed border border-transparent hover:border-slate-200 transition-colors"
                        >
                          <Eraser size={13} />
                        </button>
                        <button
                          data-testid={`delete-comment-${s._id}`}
                          onClick={() => deleteComment(s)}
                          title="Delete this comment permanently"
                          className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 border border-transparent hover:border-rose-200 transition-colors"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </td>
                  {roofTypes.map(rt => {
                    const checked = set.has(rt);
                    const isSuggested = suggested.has(rt) && !checked;
                    return (
                      <td key={rt} className="px-2 py-3 text-center">
                        <input
                          type="checkbox"
                          data-testid={`cell-${s._id}-${rt}`}
                          checked={checked}
                          onChange={(e) => toggle(s, rt, e.target.checked)}
                          disabled={state === 'saving'}
                          className={`w-4 h-4 cursor-pointer accent-[#1E40AF] ${isSuggested ? 'ring-2 ring-violet-400 ring-offset-1' : ''}`}
                          title={isSuggested ? `Suggested: keyword match in body` : ''}
                        />
                      </td>
                    );
                  })}
                  <td className="px-3 py-3 text-center">
                    {state === 'saving' && <Loader2 size={14} className="animate-spin text-slate-400 inline" />}
                    {state === 'saved' && <Check size={14} className="text-emerald-500 inline" />}
                    {state === 'error' && <X size={14} className="text-rose-500 inline" />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Column header with a context menu for bulk tick/untick/delete.
function ColumnHeader({ roofType, busy, onTickAll, onUntickAll, onDelete }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open]);
  return (
    <th className="px-2 py-3 text-center whitespace-nowrap relative" title={roofType}>
      <div className="flex items-center justify-center gap-1">
        <span className="inline-block max-w-[80px] truncate align-middle">{roofType}</span>
        <button
          data-testid={`col-menu-${roofType}`}
          onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
          disabled={busy}
          className="text-slate-400 hover:text-[#1E40AF] disabled:opacity-50"
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> : <MoreVertical size={11} />}
        </button>
      </div>
      {open && (
        <div className="absolute top-full right-0 mt-1 w-48 bg-white border border-[#E2E2DF] rounded-md shadow-xl z-20 normal-case tracking-normal text-xs font-normal text-left">
          <button data-testid={`col-tick-${roofType}`} onClick={(e) => { e.stopPropagation(); setOpen(false); onTickAll(); }} className="w-full px-3 py-2 text-left text-slate-700 hover:bg-emerald-50 flex items-center gap-2 border-b border-slate-50">
            <Check size={12} className="text-emerald-600" /> Tick all rows
          </button>
          <button data-testid={`col-untick-${roofType}`} onClick={(e) => { e.stopPropagation(); setOpen(false); onUntickAll(); }} className="w-full px-3 py-2 text-left text-slate-700 hover:bg-slate-50 flex items-center gap-2 border-b border-slate-50">
            <X size={12} className="text-slate-500" /> Untick all rows
          </button>
          <button data-testid={`col-delete-${roofType}`} onClick={(e) => { e.stopPropagation(); setOpen(false); onDelete(); }} className="w-full px-3 py-2 text-left text-rose-600 hover:bg-rose-50 flex items-center gap-2">
            <Trash2 size={12} /> Delete roof type
          </button>
        </div>
      )}
    </th>
  );
}
